import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslate} from "../../../../../../hooks/translate.hook";
import {Alert, Button, Space, Table} from "antd";
import {AgreementData} from "./common/model";
import {columnsFactory} from "./ui.input.widget.warehouses-agreements.columns";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {warehousesService} from "../../../../../../services/warehouses/warehouses.service";
import {AgreementEditModal} from "./modal/edit/agreement.edit.modal";
import "./ui.input.widget.warehouses-agreements.scss"

interface WarehousesAgreementsProps {
    value?: AgreementData[];
    onChange?: (value: AgreementData[]) => void;
}

type MoveDirection = -1 | 1;

const normalizeSort = (items: AgreementData[]): AgreementData[] => {
    return items.map((item, index) => ({
        ...item,
        sort: index
    }));
};

export const UiInputWidgetWarehousesAgreements: React.FC<WarehousesAgreementsProps> = ({value, onChange}) => {
    const t = useTranslate();

    const [warehouses, setWarehouses] = useState<Record<string, string>>({});

    const notUsedWarehouses: Record<string, string> = useMemo(
        () => Object.fromEntries(
            Object.entries(warehouses).filter(
                ([code]) => !value?.some(item => String(item.warehouse) === code)
            )
        ),
        [warehouses, value]
    );

    const emitChange = useCallback(
        (items: AgreementData[]) => {
            onChange?.(normalizeSort(items));
        },
        [onChange]
    );


    const [editModalShown, setEditModalShown] = useState<boolean>(false);
    const [editModalItem, setEditModalItem] = useState<AgreementData | null>(null);

    const orderedValue = useMemo(() => {
        return (value ?? [])
            .map((item, originalIndex) => ({
                item,
                order: item.sort ?? originalIndex
            }))
            .sort((left, right) => left.order - right.order)
            .map(({item}) => item);
    }, [value]);

    const onClose = () => {
        setEditModalItem(null);
        setEditModalShown(false);
    }


    const onMove = useCallback(
        (index: number, direction: MoveDirection) => {
            const targetIndex = index + direction;

            if (
                targetIndex < 0 ||
                targetIndex >= orderedValue.length
            ) {
                return;
            }

            const updatedData = [...orderedValue];

            [
                updatedData[index],
                updatedData[targetIndex]
            ] = [
                updatedData[targetIndex],
                updatedData[index]
            ];

            emitChange(updatedData);
        },
        [orderedValue, emitChange]
    );

    const onDelete = useCallback(
        (index: number) => {
            const updatedData = orderedValue.filter(
                (_, currentIndex) => currentIndex !== index
            );

            emitChange(updatedData);
        },
        [orderedValue, emitChange]
    );

    const onComplete = useCallback(
        (item: AgreementData) => {
            const updatedData = editModalItem
                ? orderedValue.map(currentItem =>
                    currentItem.warehouse === editModalItem.warehouse
                        ? {
                            ...item,
                            sort: currentItem.sort
                        }
                        : currentItem
                )
                : [
                    ...orderedValue,
                    {
                        ...item,
                        sort: orderedValue.length
                    }
                ];

            emitChange(updatedData);
            onClose();
        },
        [editModalItem, orderedValue, emitChange, onClose]
    );

    const onEdit = useCallback((item: AgreementData | null) => {
        setEditModalItem(item);
        setEditModalShown(true);
    }, []);

    const columns = useMemo(
        () =>
            columnsFactory(
                t,
                warehouses,
                onEdit,
                onMove,
                onDelete,
                orderedValue.length
            ),
        [
            t,
            warehouses,
            onEdit,
            onMove,
            onDelete,
            orderedValue.length
        ]
    );

    useEffect(() => {
        warehousesService.getAllWarehouses().then(values => {
            setWarehouses(Object.fromEntries(
                values.map(({ id, name }) => [id, name])));
        });
    }, []);

    if (Object.keys(warehouses).length === 0) {
        return <></>
    }

    return (
        <>
            <Space size={[20, 20]} direction={"vertical"} style={{width: "100%"}}>
                {orderedValue.length > 0 ? (
                    <Table
                        className={"agreements-table"}
                        columns={columns}
                        dataSource={orderedValue}
                        pagination={false}
                        rowKey="warehouse"
                        rowClassName={(item) => item.readonly ? "agreements-table__row--readonly" : ""}
                        scroll={{x: 640}}
                        tableLayout="fixed"
                    />
                ) : (
                    <Alert message={
                        <>No warehouses have been added yet. <a onClick={() => onEdit(null)}>Click here</a> to add your first warehouse.</>
                    } />
                )}
                <Button
                    key={0}
                    icon={<FontAwesomeIcon icon={["fas", "add"]} />}
                    type={"primary"}
                    onClick={() => onEdit(null)}
                    disabled={
                        Object.keys(notUsedWarehouses).length === 0
                    }
                >
                    {t("ADD")}
                </Button>
            </Space>
            {editModalShown ? (
                <AgreementEditModal
                    item={editModalItem}
                    onClose={onClose}
                    onComplete={onComplete}
                    warehouses={notUsedWarehouses}
                />
            ) : null}
        </>
    )
}
